import { useState } from 'react';
import { Icone } from '../../design/Icone';
import { DIAS_DA_FOLGA, diaMes, hojeBrasilia, somarDias } from '../../escala';
import type { FolgaFixa, PessoaDaEscala, TurnoDaEscala } from '../../escala';
import { celulasDaFolgaFixaWeb, diaCurtoDaSemana, proximoDomingo } from './folga-fixa';

/**
 * FOLGA FIXA da pessoa (dono 24/10): "Vitória folga toda segunda e trabalha 2 domingos e folga 1 —
 * fica segunda, segunda, domingo, segunda; e o outro modo é só a folga fixa de segunda a sábado e
 * o domingo deixa que ele (o Carlos) coloca."
 */

const MODOS: readonly (readonly ['' | FolgaFixa['modo'], string, string])[] = [
  [
    'FIXA_DOMINGO',
    'Folga fixa + domingo',
    'Folga no dia fixo e 1 domingo a cada 3. Na semana do domingo de folga, trabalha no dia fixo.',
  ],
  ['FIXA', 'Só folga fixa', 'Folga no dia fixo. O domingo o Carlos põe na mão.'],
  ['DIAS', 'Dias fixos', 'Folga nos mesmos dias toda semana — ex.: todo sábado e domingo.'],
  ['', 'Sem folga fixa', 'Tudo na mão, no quadro.'],
];

export function EditorDaFolgaFixa({
  pessoa,
  turnos,
  aoFechar,
  aoSalvar,
  embutido = false,
}: {
  pessoa: PessoaDaEscala;
  turnos: readonly TurnoDaEscala[];
  aoFechar: () => void;
  aoSalvar: (f: FolgaFixa | null) => Promise<void>;
  /** Na ficha do funcionário: sem a janela em volta. */
  embutido?: boolean;
}) {
  const hoje = hojeBrasilia();
  const f0 = pessoa.folgaFixa;
  const [modo, setModo] = useState<'' | FolgaFixa['modo']>(f0?.modo ?? 'FIXA_DOMINGO');
  const [dia, setDia] = useState(f0?.dia || 1);
  const [diasFixos, setDiasFixos] = useState<number[]>(
    f0?.modo === 'DIAS' ? [...f0.diasFixos] : [6, 0],
  );
  const [domingo, setDomingo] = useState(f0?.domingoBase ?? proximoDomingo(hoje));
  const [ciclo, setCiclo] = useState(f0?.cicloDomingo ?? 3);
  const [turno, setTurno] = useState(f0?.turnoPadrao ?? '');
  const [trabalhaFeriado, setTrabalhaFeriado] = useState(f0?.trabalhaFeriado ?? true);
  const [folgaDupla, setFolgaDupla] = useState(f0?.folgaDupla ?? false);
  const [salvando, setSalvando] = useState(false);
  const ehDomingo = new Date(`${domingo}T12:00:00Z`).getUTCDay() === 0;
  const f: FolgaFixa | null = modo
    ? {
        modo,
        dia,
        diasFixos: modo === 'DIAS' ? diasFixos : [],
        domingoBase: modo === 'FIXA_DOMINGO' ? domingo : null,
        cicloDomingo: ciclo,
        turnoPadrao: turno || null,
        trabalhaFeriado,
        folgaDupla: modo === 'FIXA_DOMINGO' && folgaDupla,
      }
    : null;
  // prévia: as folgas das próximas 6 semanas, do jeito que o botão do quadro vai pôr
  const previa =
    f && (modo !== 'FIXA_DOMINGO' || ehDomingo) && (modo !== 'DIAS' || diasFixos.length > 0)
      ? celulasDaFolgaFixaWeb(f, hoje, somarDias(hoje, 41))
          .filter((m) => m.turno === 'F')
          .map((m) => m.dia)
      : [];

  const corpo = (
    <>
      <div className="qh-tipos" role="radiogroup" aria-label="Modo da folga">
        {MODOS.map(([id, rot, txt]) => (
          <button
            key={id || 'nada'}
            type="button"
            role="radio"
            aria-checked={modo === id}
            className={`qh-tipo-opcao${modo === id ? ' qh-tipo-opcao-on' : ''}`}
            onClick={() => setModo(id)}
          >
            <strong>{rot}</strong>
            <small>{txt}</small>
          </button>
        ))}
      </div>
      {modo && (
        <>
          {modo === 'DIAS' && (
            <fieldset className="qh-dias-fixos">
              <legend>Folga toda semana em</legend>
              {[1, 2, 3, 4, 5, 6, 0].map((d) => (
                <label key={d} className={diasFixos.includes(d) ? 'qh-dia-on' : undefined}>
                  <input
                    type="checkbox"
                    checked={diasFixos.includes(d)}
                    onChange={(e) =>
                      setDiasFixos((l) => (e.target.checked ? [...l, d] : l.filter((x) => x !== d)))
                    }
                  />
                  {d === 0 ? 'domingo' : DIAS_DA_FOLGA[d]}
                </label>
              ))}
            </fieldset>
          )}
          <div className="qh-linha-campos">
            {modo !== 'DIAS' && (
              <label className="qh-campo">
                Folga fixa toda
                <select value={dia} onChange={(e) => setDia(Number(e.target.value))}>
                  {[1, 2, 3, 4, 5, 6].map((d) => (
                    <option key={d} value={d}>
                      {DIAS_DA_FOLGA[d]}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label className="qh-campo">
              Turno nos outros dias
              <select value={turno} onChange={(e) => setTurno(e.target.value)}>
                <option value="">Não preencher (só as folgas)</option>
                {turnos.map((t) => (
                  <option key={t.codigo} value={t.codigo}>
                    {t.rotulo}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {modo === 'FIXA_DOMINGO' && (
            <div className="qh-linha-campos">
              <label className="qh-campo">
                Um domingo em que folga
                <input type="date" value={domingo} onChange={(e) => setDomingo(e.target.value)} />
                {!ehDomingo && <small className="qh-erro-campo">Escolha um domingo.</small>}
              </label>
              <label className="qh-campo">
                Folga 1 domingo a cada
                <select value={ciclo} onChange={(e) => setCiclo(Number(e.target.value))}>
                  {[2, 3, 4, 5, 6].map((n) => (
                    <option key={n} value={n}>
                      {n} semanas (trabalha {n - 1}, folga 1)
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}
          {modo === 'FIXA_DOMINGO' && (
            <label className="qh-check">
              <input
                type="checkbox"
                checked={folgaDupla}
                onChange={(e) => setFolgaDupla(e.target.checked)}
              />
              <span>
                <strong>Na semana do domingo de folga, folga também no dia fixo</strong>
                <small>
                  {folgaDupla
                    ? 'Nessa semana são 2 folgas: o domingo e o dia fixo.'
                    : 'Nessa semana o domingo substitui o dia fixo (trabalha no dia fixo).'}
                </small>
              </span>
            </label>
          )}
          <label className="qh-check">
            <input
              type="checkbox"
              checked={trabalhaFeriado}
              onChange={(e) => setTrabalhaFeriado(e.target.checked)}
            />
            <span>
              <strong>Trabalha em feriado</strong>
              <small>
                {trabalhaFeriado
                  ? 'No feriado trabalha no turno dele — e ganha folga a compensar.'
                  : 'Nos feriados fica FR (feriado, não trabalha). A folga fixa do dia continua F.'}
              </small>
            </span>
          </label>
          {previa.length > 0 && (
            <p className="qh-previa">
              <strong>Próximas folgas:</strong>{' '}
              {previa.map((d) => `${diaCurtoDaSemana(d)} ${diaMes(d)}`).join(' · ')}
            </p>
          )}
        </>
      )}
      <footer>
        <button type="button" className="qh-btn qh-btn-sec" onClick={aoFechar}>
          Cancelar
        </button>
        <button
          type="button"
          className="qh-btn qh-btn-pri"
          disabled={
            salvando ||
            (modo === 'FIXA_DOMINGO' && !ehDomingo) ||
            (modo === 'DIAS' && (diasFixos.length === 0 || diasFixos.length > 6))
          }
          onClick={() => {
            setSalvando(true);
            void aoSalvar(f);
          }}
        >
          Salvar
        </button>
      </footer>
    </>
  );
  // dentro da ficha do funcionário (o lápis): só o formulário, sem a janela em volta
  if (embutido) return <div className="qh-ficha-parte">{corpo}</div>;
  return (
    <div className="qh-modal-fundo" onClick={aoFechar} role="presentation">
      <div
        className="qh-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`Folga fixa de ${pessoa.nome}`}
        onClick={(e) => e.stopPropagation()}
      >
        <header>
          <div>
            <h2>Folga fixa · {pessoa.nome}</h2>
            <p>
              Ao salvar, o quadro segue isto sozinho de hoje até 6 meses à frente (férias, faltas,
              atestados, folgas de feriado e pedidos aprovados ficam).
            </p>
          </div>
          <button type="button" className="qh-btn-icone" onClick={aoFechar} aria-label="Fechar">
            <Icone nome="x" tamanho={16} />
          </button>
        </header>
        {corpo}
      </div>
    </div>
  );
}
